/**
 * Hybrid Search - Unified text + semantic search
 *
 * Combines exact text matching (MB API /manage/products) with semantic
 * AI search (gpu1.floorplanner.com/search), then merges results using
 * Reciprocal Rank Fusion (RRF) so exact name/SKU matches float to the
 * top while semantic matches fill in the gaps.
 */

import {store} from '@/store';
import {userAtom} from '@/store/user';
import {getModelbankToken} from '@/util/misc';
import {mbApiJson, fetchWithTimeout} from '@/util/request-utils';

export interface HybridSearchProduct {
    id: string;
    name?: string;
    sku?: string;
    url?: string;
    thumbnail_url?: string;
    sources: {
        text: boolean;
        vector: boolean;
    };
}

export interface HybridSearchResult {
    products: HybridSearchProduct[];
    total: number;
    meta: {
        textResults: number;
        vectorResults: number;
        merged: number;
    };
}

interface HybridSearchOptions {
    /** Max results to return (default: 40) */
    limit?: number;
    /** Weight for text results (default: 1.5 - boosted for exact matches) */
    textWeight?: number;
    /** Weight for vector results (default: 1.0) */
    vectorWeight?: number;
}

// --- Text search (MB API) ---

interface ProductHit {
    _id: string;
    uid?: string;
    _source: {
        name: string;
        sku: string;
        [key: string]: unknown;
    };
}

interface ProductSearchResponse {
    hits: {
        hits: ProductHit[];
        total: number;
    };
}

async function textSearch(
    query: string,
    limit: number
): Promise<{id: string; name?: string; sku?: string}[]> {
    const user = store.get(userAtom);
    const token = getModelbankToken();
    if (!token || !user?.auth_token) return [];

    try {
        const data = await mbApiJson<ProductSearchResponse>(
            '/manage/products',
            {
                token,
                auth_token: user.auth_token,
                params: {per_page: limit, query},
            }
        );

        const seenIds = new Set<string>();
        return data.hits.hits
            .filter((p) => {
                const baseId = p._id.split('_')[0];
                if (seenIds.has(baseId)) return false;
                seenIds.add(baseId);
                return true;
            })
            .map((p) => ({
                id: (p.uid ?? p._id).split('_')[0],
                name: p._source.name,
                sku: p._source.sku,
            }));
    } catch {
        return [];
    }
}

// --- Semantic search (gpu1) ---

interface Gpu1Result {
    id: string;
    name?: string;
    sku?: string;
    [key: string]: unknown;
}

const GPU1_BASE = import.meta.env.DEV
    ? '/gpu1'
    : 'https://gpu1.floorplanner.com';

async function vectorSearch(
    query: string
): Promise<{id: string; name?: string; sku?: string}[]> {
    const token = getModelbankToken();
    if (!token) return [];

    try {
        const response = await fetchWithTimeout(
            `${GPU1_BASE}/search`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({query}),
            },
            30000
        );

        if (!response.ok) return [];

        const data = (await response.json()) as {results?: Gpu1Result[]};
        const items = data.results ?? [];

        const seenIds = new Set<string>();
        return items
            .filter((p) => {
                const baseId = String(p.id).split('_')[0];
                if (seenIds.has(baseId)) return false;
                seenIds.add(baseId);
                return true;
            })
            .map((p) => ({
                id: String(p.id).split('_')[0],
                name: p.name,
                sku: p.sku,
            }));
    } catch {
        return [];
    }
}

// --- RRF merge ---

const RRF_K = 60; // standard RRF constant

function rrfMerge(
    textResults: {id: string; name?: string; sku?: string}[],
    vectorResults: {id: string; name?: string; sku?: string}[],
    textWeight: number,
    vectorWeight: number,
    limit: number
): HybridSearchProduct[] {
    const scores = new Map<
        string,
        {
            score: number;
            text: boolean;
            vector: boolean;
            name?: string;
            sku?: string;
        }
    >();

    for (let i = 0; i < textResults.length; i++) {
        const p = textResults[i];
        const existing = scores.get(p.id) ?? {
            score: 0,
            text: false,
            vector: false,
            name: p.name,
            sku: p.sku,
        };
        existing.score += textWeight / (RRF_K + i + 1);
        existing.text = true;
        if (p.name) existing.name = p.name;
        if (p.sku) existing.sku = p.sku;
        scores.set(p.id, existing);
    }

    for (let i = 0; i < vectorResults.length; i++) {
        const p = vectorResults[i];
        const existing = scores.get(p.id) ?? {
            score: 0,
            text: false,
            vector: false,
            name: p.name,
            sku: p.sku,
        };
        existing.score += vectorWeight / (RRF_K + i + 1);
        existing.vector = true;
        if (p.name && !existing.name) existing.name = p.name;
        if (p.sku && !existing.sku) existing.sku = p.sku;
        scores.set(p.id, existing);
    }

    return Array.from(scores.entries())
        .sort((a, b) => b[1].score - a[1].score)
        .slice(0, limit)
        .map(([id, data]) => ({
            id,
            name: data.name,
            sku: data.sku,
            sources: {text: data.text, vector: data.vector},
        }));
}

// --- Public API ---

/**
 * Perform hybrid search combining text and semantic search.
 *
 * Runs both searches in parallel, then merges with RRF ranking so
 * exact name/SKU matches float to the top.
 */
export async function hybridSearch(
    query: string,
    options: HybridSearchOptions = {}
): Promise<HybridSearchResult> {
    if (!query.trim()) {
        return {
            products: [],
            total: 0,
            meta: {textResults: 0, vectorResults: 0, merged: 0},
        };
    }

    const limit = options.limit ?? 40;
    const textWeight = options.textWeight ?? 1.5;
    const vectorWeight = options.vectorWeight ?? 1.0;

    const [textResults, vectorResults] = await Promise.all([
        textSearch(query, limit),
        vectorSearch(query),
    ]);

    const merged = rrfMerge(
        textResults,
        vectorResults,
        textWeight,
        vectorWeight,
        limit
    );

    return {
        products: merged,
        total: merged.length,
        meta: {
            textResults: textResults.length,
            vectorResults: vectorResults.length,
            merged: merged.length,
        },
    };
}
